import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { RuteroVertical, VOUCHER_TEXT_LANGUAGES, VoucherTextLanguage } from '@bandido/shared/types';

import { VOUCHER_PREVIEW_VERTICALS } from '../voucherPreview';

const KEY = 'vouchers.config';

interface PreviewPanelProps {
  vertical: RuteroVertical;
  /** El conector con el que se dibuja, cuando se está mirando uno. */
  connector?: string;
  onClearConnector?: () => void;
  lang: VoucherTextLanguage;
  url?: string;
  loading: boolean;
  /** Por qué no hay PDF, cuando no lo hay. */
  messageKey: string | null;
  /** Lo que SÍ existe para ese producto, cuando no emite voucher. */
  alternativeKey?: string;
  onOpen: () => void;
  /** Volver a pedir el mismo borrador, cuando el fallo fue pasajero. */
  onRetry?: () => void;
  onVertical: (vertical: RuteroVertical) => void;
  onLang: (lang: VoucherTextLanguage) => void;
}

/**
 * LA COLUMNA DERECHA: el documento que va a salir, dibujado con lo que hay en
 * el formulario. Es el mismo motor y los mismos textos que el PDF real
 * (`POST /vouchers/preview` con la reserva de ejemplo del vertical), así que lo
 * que se ve acá es lo que recibe el pasajero.
 *
 * La identidad que dibuja es la GUARDADA: el endpoint no recibe logos ni color
 * en borrador. La pantalla lo dice en vez de dejar creer que el logo que se
 * acaba de subir ya está adentro.
 */
export const PreviewPanel: FC<PreviewPanelProps> = ({
  vertical,
  connector,
  onClearConnector,
  lang,
  url,
  loading,
  messageKey,
  alternativeKey,
  onOpen,
  onRetry,
  onVertical,
  onLang,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Stack gap={2}>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <TextField
          select
          size="small"
          label={t(`${KEY}.preview.seeAs`)}
          value={vertical}
          onChange={(event) => onVertical(event.target.value as RuteroVertical)}
          sx={{ minWidth: 180 }}
        >
          {VOUCHER_PREVIEW_VERTICALS.map((name) => (
            <MenuItem key={name} value={name}>
              {/* Los nombres de los verticales ya están traducidos: no se
                  duplican acá. */}
              {t(`rutero.verticals.${name}`)}
            </MenuItem>
          ))}
        </TextField>
        {/* "Ver como {vertical} · {conector}": el texto de un proveedor solo sale
            en SUS reservas, así que la vista previa dice cuál está usando. */}
        {connector && (
          <Chip size="small" label={connector} onDelete={onClearConnector} aria-label={t(`${KEY}.preview.seeAs`)} />
        )}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={lang}
          onChange={(_event, value) => value && onLang(value as VoucherTextLanguage)}
        >
          {VOUCHER_TEXT_LANGUAGES.map((code) => (
            <ToggleButton key={code} value={code}>
              {t(`${KEY}.texts.lang.${code}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {loading && <CircularProgress size={18} />}
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t(`${KEY}.preview.savedIdentity`)}
      </Typography>

      {messageKey && (
        <Alert
          severity="info"
          /* Un fallo pasajero se reintenta con el mismo borrador: sin esto, la
             única salida era tocar el texto o recargar la página —y perder lo
             que no estuviera guardado—. */
          action={
            onRetry ? (
              <Button size="small" onClick={onRetry}>
                {t(`${KEY}.preview.retry`)}
              </Button>
            ) : undefined
          }
        >
          {t(messageKey)}
          {/* La alternativa va ADEMÁS: sola no dice que este producto no
              emite documento. */}
          {alternativeKey ? ` ${t(alternativeKey)}` : ''}
        </Alert>
      )}

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          overflow: 'hidden',
          minHeight: 420,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {url ? (
          <iframe
            src={url}
            title={t(`${KEY}.preview.title`)}
            style={{ width: '100%', height: 420, border: 0 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center" p={3}>
            {t(`${KEY}.preview.empty`)}
          </Typography>
        )}
      </Box>

      {/* La salida cuando el visor embebido no dibuja: el navegador puede tener
          el plugin de PDF apagado, y entonces el iframe queda en blanco. */}
      <Box display="flex" justifyContent="flex-end">
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          disabled={!url}
          onClick={onOpen}
        >
          {t(`${KEY}.preview.open`)}
        </Button>
      </Box>
    </Stack>
  );
};
